import { markierTag } from './meintag-markierung'

/**
 * **Was im Feld „Festhalten" steht – gelesen** ([Z-24](../../../docs/phasen/zusatz-24-my-work.md)
 * AP 24.9, [E-453](../../../docs/ENTSCHEIDUNGEN.md#e-453)).
 *
 * Ein Feld für alles: Notiz, Aufgabe, Frage, Beschluss. Was es wird, steht am Anfang der Zeile;
 * Frist, Menschen und Tags stehen irgendwo darin. Ohne KI und ohne Netz – die Chips unter dem Feld
 * erscheinen beim Tippen.
 *
 * | Am Anfang | wird | Beispiel |
 * |---|---|---|
 * | `☐` `[]` `[ ]` `- [ ]` | Aufgabe | `☐ Kosten grob schätzen bis Do` |
 * | `?` | offene Frage | `? @Fabian wann kommen die Pläne` |
 * | `!` | Beschluss | `! POP an die Nordseite` |
 * | sonst | Notiz | `Graph liefert Termine in UTC` |
 *
 * Bei einer Aufgabe sind `bis Do`, `morgen`, `übermorgen`, `30.10.` die **Frist** und `heute` heißt
 * „für heute vornehmen" (☀) statt einer Frist. Beginnt die Aufgabe mit `@Name`, ist sie **für
 * jemand anderen** – sie wird dann nur notiert (E-433), nie von selbst zugewiesen.
 */

export type FesthaltenArt = 'notiz' | 'aufgabe' | 'frage' | 'beschluss'

export interface Festgehalten {
  art: FesthaltenArt
  /** Der Text ohne Zeichen am Anfang und – bei Aufgaben – ohne die Frist-Wendung. */
  text: string
  /** `@Name` am Anfang einer Aufgabe: für diese Person, nicht für mich. */
  fuer: string | null
  personen: string[]
  tags: string[]
  /** `YYYY-MM-DD` – nur bei Aufgaben. */
  frist: string | null
  /** `heute` in einer Aufgabe: für heute vornehmen. */
  heute: boolean
  /** Die erkannte Frist-Wendung, wie getippt – für den Chip („bis Do"). */
  fristText: string | null
}

const AUFGABE = /^(?:☐|-\s*\[\s?\]|\[\s?\])\s*/u
const FRAGE = /^\?\s*/u
const BESCHLUSS = /^!\s*/u
const PERSON = /@([\p{L}][\p{L}\p{N}._-]*)/gu
const TAG = /(?:^|\s)#([\p{L}\p{N}_-]+)/gu

const WOCHENTAGE: Record<string, number> = {
  so: 0,
  sonntag: 0,
  mo: 1,
  montag: 1,
  di: 2,
  dienstag: 2,
  mi: 3,
  mittwoch: 3,
  do: 4,
  donnerstag: 4,
  fr: 5,
  freitag: 5,
  sa: 6,
  samstag: 6,
}

const schluessel = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/**
 * **Die Frist-Wendungen** – in dieser Reihenfolge versucht, die längste zuerst, damit „bis
 * übermorgen" nicht als „morgen" gelesen wird. Wortgrenzen über Leerzeichen und Satzzeichen, weil
 * `\b` in JavaScript Umlaute nicht als Buchstaben kennt.
 */
const FRIST =
  /(?:^|[\s,(])((?:bis\s+(?:zum\s+|zu\s+)?)?(?:übermorgen|uebermorgen|morgen|heute|(?:montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag|mo|di|mi|do|fr|sa|so)\.?|\d{1,2}\.\d{1,2}\.(?:\d{2,4})?))(?=$|[\s,.;:!?)])/iu

function fristLesen(wendung: string, heute: Date): { frist: string | null; heute: boolean } {
  const ohneBis = wendung.toLowerCase().replace(/^bis\s+(?:zum\s+|zu\s+)?/u, '')
  /** Der Punkt hinter „Do." gehört nicht zum Wort – hinter „30.10." aber zum Datum. */
  const w = /^\d/u.test(ohneBis) ? ohneBis : ohneBis.replace(/\.$/u, '')
  const heuteKey = schluessel(heute)
  if (w === 'heute') return { frist: null, heute: true }
  /** „morgen" am Freitag ist Montag – dieselbe Werktag-Rechnung wie beim ☀ Markieren. */
  if (w === 'morgen') return { frist: markierTag('morgen', heuteKey), heute: false }
  if (w === 'übermorgen' || w === 'uebermorgen')
    return { frist: markierTag('uebermorgen', heuteKey), heute: false }
  const tag = WOCHENTAGE[w]
  if (tag !== undefined) {
    /** Der nächste dieser Wochentage nach heute – „bis Do" am Donnerstag ist in einer Woche. */
    const d = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate(), 12)
    const abstand = (tag - d.getDay() + 7) % 7 || 7
    d.setDate(d.getDate() + abstand)
    return { frist: schluessel(d), heute: false }
  }
  const datum = /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})?$/u.exec(w)
  if (datum) {
    const t = Number(datum[1])
    const m = Number(datum[2])
    let j = datum[3] ? Number(datum[3]) : heute.getFullYear()
    if (j < 100) j += 2000
    const d = new Date(j, m - 1, t, 12)
    if (d.getMonth() !== m - 1 || d.getDate() !== t) return { frist: null, heute: false }
    /** Ohne Jahr und schon vorbei: dann ist das nächste Jahr gemeint. */
    if (!datum[3] && schluessel(d) < heuteKey) d.setFullYear(j + 1)
    return { frist: schluessel(d), heute: false }
  }
  return { frist: null, heute: false }
}

export function festhaltenLesen(roh: string, heute: Date): Festgehalten {
  let text = roh.trim()
  let art: FesthaltenArt = 'notiz'
  if (AUFGABE.test(text)) {
    art = 'aufgabe'
    text = text.replace(AUFGABE, '')
  } else if (FRAGE.test(text)) {
    art = 'frage'
    text = text.replace(FRAGE, '')
  } else if (BESCHLUSS.test(text)) {
    art = 'beschluss'
    text = text.replace(BESCHLUSS, '')
  }

  const personen = [...text.matchAll(PERSON)].map((m) => m[1]!)
  const tags = [...text.matchAll(TAG)].map((m) => m[1]!)
  const fuerTreffer = art === 'aufgabe' ? /^@([\p{L}][\p{L}\p{N}._-]*)/u.exec(text) : null

  let frist: string | null = null
  let heuteMarke = false
  let fristText: string | null = null
  if (art === 'aufgabe') {
    const f = FRIST.exec(text)
    if (f?.[1]) {
      const gelesen = fristLesen(f[1], heute)
      if (gelesen.frist || gelesen.heute) {
        frist = gelesen.frist
        heuteMarke = gelesen.heute
        fristText = f[1]
        text = (text.slice(0, f.index) + text.slice(f.index + f[0].length))
          .replace(/\s{2,}/gu, ' ')
          .replace(/[\s,;:-]+$/u, '')
          .trim()
      }
    }
  }

  return {
    art,
    text: text.trim(),
    fuer: fuerTreffer?.[1] ?? null,
    personen,
    tags,
    frist,
    heute: heuteMarke,
    fristText,
  }
}
